import React from 'react';
import { PiggyBank, Landmark, Home, GraduationCap, Heart, Briefcase, Handshake, Users } from 'lucide-react';
import ToolResult from '../../../../../components/ui/ToolResult';
import { formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент отображения детализации налоговых вычетов
 */
const DeductionsSection = ({ taxDetails, formData, deductionLimits }) => {
  if (!taxDetails || !formData.includeDeductions) return null;

  const { deductionsBreakdown } = taxDetails;
  const hasDeductions = 
    deductionsBreakdown.standard > 0 || 
    deductionsBreakdown.social > 0 || 
    deductionsBreakdown.property > 0 || 
    deductionsBreakdown.investment > 0;

  if (!hasDeductions) return null;

  return (
    <ToolResult title="Детализация налоговых вычетов">
      <div className="mb-4">
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Налоговые вычеты уменьшают сумму дохода, с которой удерживается налог, что позволяет снизить налоговую нагрузку.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {/* Стандартные вычеты */}
        {deductionsBreakdown.standard > 0 && (
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-100 dark:border-blue-800">
            <div className="flex items-start gap-3">
              <div className="mt-1 flex-shrink-0">
                <Users className="w-6 h-6 text-blue-500" />
              </div>
              <div>
                <h4 className="text-base font-medium text-gray-900 dark:text-white mb-1">Стандартные вычеты</h4>
                <div className="text-xl font-bold text-blue-700 dark:text-blue-300 mb-2">
                  {formatCurrency(deductionsBreakdown.standard)}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                  {formData.hasChildren && (
                    <p>
                      Вычет на {formData.childrenCount} детей: {formatCurrency(formData.childrenCount <= 2 
                        ? formData.childrenCount * deductionLimits.standard.children.regular * 12 
                        : (2 * deductionLimits.standard.children.regular + (Number(formData.childrenCount) - 2) * deductionLimits.standard.children.third_plus) * 12)}
                    </p>
                  )}
                  {formData.hasChildrenDisabled && (
                    <p>
                      Вычет на {formData.childrenDisabledCount} детей-инвалидов: {formatCurrency(Number(formData.childrenDisabledCount) * deductionLimits.standard.children.disabled * 12)}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Социальные вычеты */}
        {deductionsBreakdown.social > 0 && (
          <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 border border-green-100 dark:border-green-800">
            <div className="flex items-start gap-3">
              <div className="mt-1 flex-shrink-0">
                <Heart className="w-6 h-6 text-green-500" />
              </div>
              <div>
                <h4 className="text-base font-medium text-gray-900 dark:text-white mb-1">Социальные вычеты</h4>
                <div className="text-xl font-bold text-green-700 dark:text-green-300 mb-2">
                  {formatCurrency(deductionsBreakdown.social)}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                  {formData.hasEducation && (
                    <p>
                      На обучение: {formatCurrency(Math.min(Number(formData.educationExpenseAmount), deductionLimits.social.education))}
                    </p>
                  )}
                  {formData.hasMedical && (
                    <p>
                      На лечение: {formatCurrency(Math.min(Number(formData.medicalExpenseAmount), deductionLimits.social.medical))}
                    </p>
                  )}
                  {formData.hasInsurance && (
                    <p>
                      На страхование: {formatCurrency(Math.min(Number(formData.insuranceExpenseAmount), deductionLimits.social.insurance))}
                    </p>
                  )}
                  {formData.hasIpn && (
                    <p>
                      На пенсионный счет (ИПН): {formatCurrency(Math.min(Number(formData.ipnAmount), deductionLimits.social.ipn))}
                    </p>
                  )}
                  {formData.hasCharity && (
                    <p>
                      На благотворительность: {formatCurrency(Math.min(Number(formData.charityExpenseAmount), Number(formData.incomeAmount) * 0.25))}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Имущественные вычеты */}
        {deductionsBreakdown.property > 0 && (
          <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4 border border-orange-100 dark:border-orange-800">
            <div className="flex items-start gap-3">
              <div className="mt-1 flex-shrink-0">
                <Home className="w-6 h-6 text-orange-500" />
              </div>
              <div>
                <h4 className="text-base font-medium text-gray-900 dark:text-white mb-1">Имущественные вычеты</h4>
                <div className="text-xl font-bold text-orange-700 dark:text-orange-300 mb-2">
                  {formatCurrency(deductionsBreakdown.property)}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                  {formData.hasMortgage && (
                    <p>
                      Проценты по ипотеке: {formatCurrency(Math.min(Number(formData.mortgageInterestAmount), deductionLimits.property.mortgage_interest))}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Инвестиционные вычеты */}
        {deductionsBreakdown.investment > 0 && (
          <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 border border-purple-100 dark:border-purple-800">
            <div className="flex items-start gap-3">
              <div className="mt-1 flex-shrink-0">
                <Briefcase className="w-6 h-6 text-purple-500" />
              </div>
              <div>
                <h4 className="text-base font-medium text-gray-900 dark:text-white mb-1">Инвестиционные вычеты</h4>
                <div className="text-xl font-bold text-purple-700 dark:text-purple-300 mb-2">
                  {formatCurrency(deductionsBreakdown.investment)}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                  {formData.hasInvestment && (
                    <p>
                      Инвестиционный вычет (ИИС): {formatCurrency(Math.min(Number(formData.investmentExpenseAmount), deductionLimits.investment.iis_type_a))}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Информационный блок о получении вычетов */}
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
        <div className="flex items-start gap-3">
          <div className="mt-0.5">
            <Landmark className="w-5 h-5 text-indigo-500" />
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Как получить налоговые вычеты</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              Для получения вычетов необходимо подать декларацию 3-НДФЛ в налоговую инспекцию. Это можно сделать:
            </p>
            <ul className="text-xs text-gray-600 dark:text-gray-400 list-disc pl-4 space-y-1">
              <li>Через личный кабинет налогоплательщика на сайте ФНС России</li>
              <li>В отделении налоговой инспекции лично</li>
              <li>По почте с описью вложения</li>
              <li>Через представителя по нотариальной доверенности</li>
            </ul>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
              Срок подачи декларации — до 30 апреля года, следующего за отчетным. Но для получения вычетов декларацию можно подать в течение 3 лет после окончания отчетного года.
            </p>
          </div>
        </div>
      </div>
    </ToolResult>
  );
};

export default DeductionsSection;